import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI } from '../services/api';

function TestDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [test, setTest] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [allowedStudents, setAllowedStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadTestDetails();
    }, [id]);

    const loadTestDetails = async () => {
        try {
            const response = await testsAPI.getTestDetails(id);
            setTest(response.data.test);
            setQuestions(response.data.questions);
            setAllowedStudents(response.data.allowed_students);
        } catch (err) {
            setError('Fehler beim Laden der Test-Details');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div style={styles.loading}>Lädt...</div>;
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>{error}</div>
                <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
                    ← Zurück zum Dashboard
                </button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>{test.title}</h1>
                <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
                    ← Zurück
                </button>
            </div>

            <div style={styles.section}>
                <h2>Test-Informationen</h2>
                <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                        <strong>Beschreibung:</strong>
                        <p>{test.description || 'Keine Beschreibung'}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Passwort:</strong>
                        <p>{test.test_password}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Zeitlimit:</strong>
                        <p>{test.time_limit ? `${test.time_limit} Minuten` : 'Kein Limit'}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Automatische Bewertung:</strong>
                        <p>{test.auto_grade ? 'Ja' : 'Nein'}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Status:</strong>
                        <p>{test.is_active ? 'Aktiv' : 'Inaktiv'}</p>
                    </div>
                    <div style={styles.infoItem}>
                        <strong>Erstellt am:</strong>
                        <p>{new Date(test.created_at).toLocaleString('de-DE')}</p>
                    </div>
                </div>
            </div>

            <div style={styles.section}>
                <h2>Erlaubte Schüler ({allowedStudents.length})</h2>
                <div style={styles.studentsList}>
                    {allowedStudents.map((student, index) => (
                        <div key={index} style={styles.studentItem}>
                            {student}
                        </div>
                    ))}
                </div>
            </div>

            <div style={styles.section}>
                <h2>Fragen ({questions.length})</h2>
                {questions.map((question, index) => (
                    <div key={question.id} style={styles.questionCard}>
                        <div style={styles.questionHeader}>
                            <h3>Frage {index + 1}</h3>
                            <span style={styles.badge}>{getQuestionTypeName(question.question_type)}</span>
                            <span style={styles.points}>{question.points} Punkt(e)</span>
                        </div>
                        <p style={styles.questionText}>{question.question_text}</p>
                        
                        {question.question_type === 'multiple_choice' && question.options && (
                            <div style={styles.options}>
                                {JSON.parse(question.options).map((option, i) => (
                                    <div 
                                        key={i} 
                                        style={{
                                            ...styles.option,
                                            ...(option === JSON.parse(question.correct_answer) ? styles.correctOption : {})
                                        }}
                                    >
                                        {option} {option === JSON.parse(question.correct_answer) && '✓'}
                                    </div>
                                ))}
                            </div>
                        )}

                        {question.question_type === 'true_false' && (
                            <div style={styles.correctAnswer}>
                                Richtige Antwort: {JSON.parse(question.correct_answer) ? 'Wahr' : 'Falsch'}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={styles.actions}>
                <button 
                    onClick={() => navigate(`/test/${id}/submissions`)}
                    style={styles.submissionsButton}
                >
                    Abgaben anzeigen
                </button>
            </div>
        </div>
    );
}

function getQuestionTypeName(type) {
    const names = {
        'multiple_choice': 'Multiple Choice',
        'true_false': 'Wahr/Falsch',
        'free_text': 'Freitext',
        'matching': 'Zuordnung'
    };
    return names[type] || type;
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '900px',
        margin: '0 auto',
    },
    loading: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.2rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    backButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    section: {
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
    },
    infoItem: {
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
    },
    studentsList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '0.5rem',
    },
    studentItem: {
        padding: '0.75rem',
        backgroundColor: '#e9ecef',
        borderRadius: '4px',
    },
    questionCard: {
        padding: '1.5rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '1rem',
        border: '1px solid #dee2e6',
    },
    questionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
    },
    badge: {
        padding: '0.25rem 0.75rem',
        backgroundColor: '#007bff',
        color: 'white',
        borderRadius: '12px',
        fontSize: '0.85rem',
    },
    points: {
        marginLeft: 'auto',
        color: '#666',
        fontSize: '0.9rem',
    },
    questionText: {
        fontSize: '1.1rem',
        marginBottom: '1rem',
    },
    options: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    option: {
        padding: '0.75rem',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
    },
    correctOption: {
        backgroundColor: '#d4edda',
        borderColor: '#28a745',
        fontWeight: 'bold',
    },
    correctAnswer: {
        padding: '0.75rem',
        backgroundColor: '#d4edda',
        borderRadius: '4px',
        color: '#155724',
        fontWeight: 'bold',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    actions: {
        textAlign: 'center',
    },
    submissionsButton: {
        padding: '1rem 2rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
};

export default TestDetails;
